import type {Dispatch, ReactNode, SetStateAction} from 'react'
import {useState} from 'react'
import type {LDReactClient, LDReactClientContextValue} from '@launchdarkly/react-sdk'
import {
  createClient,
  LDReactContext,
  useBoolVariation,
  useInitializationStatus,
  useNumberVariation,
  useStringVariation
} from '@launchdarkly/react-sdk'
import {exhaustive} from '@freckle/exhaustive'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {equalExtra, staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {logError} from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'
import {
  INITIALIZATION_TIMEOUT_SECONDS,
  launchDarklyClientSideId,
  placeholderClient,
  teacherContext
} from '@freckle/classroom/ts/common/helpers/launch-darkly'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'

type Extras = {
  teacher: TeacherAttrs | undefined
  placeholder: LDReactClient
  setValue: Dispatch<SetStateAction<LDReactClientContextValue>>
}

// Provides LaunchDarkly to the SDK's hooks without waiting for it: children render right away, and
// the client starts in the background once the teacher has loaded. Until flags load, hooks such as
// `useStringVariation` return their defaults.
export function LaunchDarklyProvider({children}: {children: ReactNode}) {
  const teacher = useTeacher().data
  const [placeholder] = useState(placeholderClient)
  const [value, setValue] = useState<LDReactClientContextValue>({
    client: placeholder,
    initializedState: 'initializing'
  })

  useSafeEffectExtraDeps<Extras>(
    ({teacher, placeholder, setValue}) => {
      if (teacher === undefined) return

      const clientSideId = launchDarklyClientSideId()
      if (clientSideId === null) {
        setValue({
          client: placeholder,
          initializedState: 'failed',
          error: new Error('No LaunchDarkly client-side ID for this environment')
        })
        return
      }

      let active = true
      const client = createClient(clientSideId, teacherContext(teacher))
      setValue({client, initializedState: 'initializing'})

      const unsubscribe = client.onInitializationStatusChange(result => {
        if (result.status === 'failed') logError(result.error)
        if (active) {
          setValue({
            client,
            context: client.getContext(),
            initializedState: result.status,
            ...(result.status === 'failed' ? {error: result.error} : {})
          })
        }
      })

      client.start({timeout: INITIALIZATION_TIMEOUT_SECONDS}).catch((error: unknown) => {
        const err = error instanceof Error ? error : new Error(String(error))
        logError(err)
        if (active) setValue({client, initializedState: 'failed', error: err})
      })

      return () => {
        active = false
        unsubscribe()
        client.close().catch(() => {})
      }
    },
    [teacher?.id],
    {
      teacher: equalExtra(teacher),
      placeholder: staticExtra(placeholder),
      setValue: staticExtra(setValue)
    }
  )

  return <LDReactContext.Provider value={value}>{children}</LDReactContext.Provider>
}

type Variation = string | number | boolean

// The type `children` receives, widened from `fallbackVariation`: with `fallbackVariation="control"`
// the variation is any string, not only "control"
type VariationOf<T extends Variation> = T extends string
  ? string
  : T extends number
    ? number
    : boolean

type Props<T extends Variation> = {
  // Key of the flag that runs the experiment
  experimentKey: string
  // Variation to show when LaunchDarkly can't evaluate the flag: it timed out, failed or has no
  // client-side ID, the flag doesn't exist, or its value is of another type. Not the variation the
  // LaunchDarkly UI serves teachers outside the experiment, which needs LaunchDarkly to be reachable.
  // Usually the control variation. Its type is the flag's type: a string, number or boolean.
  fallbackVariation: T
  // Rendered with the teacher's variation
  children: (variation: VariationOf<T>) => ReactNode
  // Rendered while flags load. Defaults to nothing.
  loading?: ReactNode
}

// Renders `children` with the teacher's variation of an experiment, and `loading` until flags have
// loaded or LaunchDarkly has failed or timed out (the variation is then `fallbackVariation`). Wrap
// only content whose rendering depends on the variation, so the rest of the page doesn't wait.
export function LaunchDarklyExperiment<T extends Variation>({
  experimentKey,
  fallbackVariation,
  children,
  loading = null
}: Props<T>) {
  const {status} = useInitializationStatus()
  if (status === 'initializing') return loading

  // TypeScript can't narrow `T` from `typeof fallbackVariation`, so widen `children` here. It's only
  // called with a value of the same type as `fallbackVariation`.
  const render = children as (variation: Variation) => ReactNode

  // Each type reads through the SDK's hook for that type, which falls back to `fallbackVariation`
  // when the flag's value is of another type
  switch (typeof fallbackVariation) {
    case 'string':
      return (
        <StringVariation
          experimentKey={experimentKey}
          fallbackVariation={fallbackVariation}
          render={render}
        />
      )
    case 'number':
      return (
        <NumberVariation
          experimentKey={experimentKey}
          fallbackVariation={fallbackVariation}
          render={render}
        />
      )
    case 'boolean':
      return (
        <BooleanVariation
          experimentKey={experimentKey}
          fallbackVariation={fallbackVariation}
          render={render}
        />
      )
    default:
      return exhaustive(fallbackVariation, 'Variation')
  }
}

// These are mounted only once flags have settled, so their hook returns the teacher's variation on
// its first render. Read before then, it would return the default for one render after flags load.
type VariationProps<V extends Variation> = {
  experimentKey: string
  fallbackVariation: V
  render: (variation: V) => ReactNode
}

function StringVariation({experimentKey, fallbackVariation, render}: VariationProps<string>) {
  return render(useStringVariation(experimentKey, fallbackVariation))
}

function NumberVariation({experimentKey, fallbackVariation, render}: VariationProps<number>) {
  return render(useNumberVariation(experimentKey, fallbackVariation))
}

function BooleanVariation({experimentKey, fallbackVariation, render}: VariationProps<boolean>) {
  return render(useBoolVariation(experimentKey, fallbackVariation))
}
